import type {ChatAskOption, ChatAskPrompt} from '../../aiMessageGenerator/utils/types';

type ResolvedAskOption = {
    option: ChatAskOption;
    number: string;
};

function optionNumber(option: ChatAskOption, position: number): string {
    return String(option.index ?? position + 1);
}

function findAskOption(
    answer: string | undefined,
    pendingAsk: ChatAskPrompt | null | undefined,
): ResolvedAskOption | null {
    const trimmed = answer?.trim();
    if(!trimmed || !pendingAsk?.options?.length) {
        return null;
    }
    // Value before number: an option that is literally named "2" is the one the producer means,
    // not the second one in the list.
    const byValue = pendingAsk.options.findIndex(opt => opt.value === trimmed || opt.label === trimmed);
    const position = byValue >= 0
        ? byValue
        : pendingAsk.options.findIndex((opt, index) => optionNumber(opt, index) === trimmed);
    if(position < 0) {
        return null;
    }
    const option = pendingAsk.options[position];
    return {option, number: optionNumber(option, position)};
}

/**
 * The server resolves a pick by its list number, and an option's own text can open with a number
 * of its own ("50 ₪") — sent as written, it reads as a pick of that list number instead of that
 * option. Anything that is not one of the options travels untouched.
 */
export function toAskAnswer(answer: string, pendingAsk: ChatAskPrompt | null | undefined): string {
    return findAskOption(answer, pendingAsk)?.number ?? answer;
}

/** The option an answer stands for, whether it arrived as its list number or as its own text. */
export function resolveAskOptionValue(
    answer: string | undefined,
    pendingAsk: ChatAskPrompt | null | undefined,
): string | null {
    return findAskOption(answer, pendingAsk)?.option.value ?? null;
}
